import { useState } from 'react';
import {
  Video, Menu, X, ArrowRight, Search, FileText,
  Camera, Film, Mic, ShieldCheck, Globe, Sparkles,
  Download, Check, Zap,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const handleLogin = async () => {
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/dashboard` },
  });
};

// ============================================================
// Navbar
// ============================================================

function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
            <Video className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-bold text-gray-900">DemoForge</span>
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-gray-600 hover:text-gray-900 transition">기능</a>
          <a href="#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition">작동방식</a>
          <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900 transition">요금</a>
          <button
            onClick={handleLogin}
            className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition"
          >
            Google로 시작하기
          </button>
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setOpen(!open)} className="md:hidden p-2 hover:bg-gray-100 rounded-lg">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-3">
          <a href="#features" onClick={() => setOpen(false)} className="block text-sm text-gray-600 hover:text-gray-900">기능</a>
          <a href="#how-it-works" onClick={() => setOpen(false)} className="block text-sm text-gray-600 hover:text-gray-900">작동방식</a>
          <a href="#pricing" onClick={() => setOpen(false)} className="block text-sm text-gray-600 hover:text-gray-900">요금</a>
          <button
            onClick={handleLogin}
            className="w-full px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition"
          >
            Google로 시작하기
          </button>
        </div>
      )}
    </nav>
  );
}

// ============================================================
// Hero
// ============================================================

function Hero() {
  return (
    <section className="bg-gradient-to-br from-purple-50 via-white to-blue-50 py-20 lg:py-32">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              AI 에이전트 6명이 만드는 데모 영상
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              URL 하나로<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
                완성되는 데모 영상
              </span>
            </h1>

            <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-lg">
              웹서비스 URL만 입력하면, AI가 사이트를 탐방하고
              시나리오를 기획하고 브라우저를 녹화하고 편집하고
              내레이션까지. 랜딩페이지용 데모 영상이 자동으로 완성됩니다.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleLogin}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-600 transition shadow-lg shadow-purple-200 flex items-center justify-center gap-2"
              >
                무료로 시작하기 <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#how-it-works"
                className="px-6 py-3 text-gray-700 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition text-center"
              >
                작동 방식 보기
              </a>
            </div>
          </div>

          {/* Right — Pipeline mockup */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 relative">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-gray-400">demoforge.io</span>
              </div>

              <div className="space-y-3">
                {[
                  { icon: Search, label: '사이트 탐방', color: 'text-blue-500 bg-blue-50', done: true },
                  { icon: FileText, label: '시나리오 기획', color: 'text-purple-500 bg-purple-50', done: true },
                  { icon: Camera, label: '브라우저 녹화', color: 'text-orange-500 bg-orange-50', done: true },
                  { icon: Film, label: '영상 편집', color: 'text-pink-500 bg-pink-50', done: false, active: true },
                  { icon: Mic, label: '내레이션 생성', color: 'text-green-500 bg-green-50', done: false },
                  { icon: ShieldCheck, label: '품질 검수', color: 'text-indigo-500 bg-indigo-50', done: false },
                ].map((step, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-xl transition ${step.active ? 'bg-gray-50 ring-1 ring-purple-200' : ''}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${step.color}`}>
                      <step.icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 flex-1">{step.label}</span>
                    {step.done && <Check className="w-4 h-4 text-green-500" />}
                    {step.active && (
                      <div className="w-4 h-4 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin" />
                    )}
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full w-3/5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all" />
              </div>

              {/* Floating badges */}
              <div className="absolute -top-3 -right-3 px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-full shadow-lg animate-bounce">
                1080p
              </div>
              <div className="absolute -bottom-3 -left-3 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full shadow-lg">
                내레이션 포함
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Features
// ============================================================

const FEATURES = [
  { icon: Search, label: '사이트 탐방', description: 'AI가 웹서비스를 구석구석 탐방하며 주요 기능과 UI 흐름을 분석합니다.', color: 'from-blue-100 to-blue-50 text-blue-600' },
  { icon: FileText, label: '시나리오 기획', description: '분석 결과를 바탕으로 설득력 있는 데모 시나리오를 자동 기획합니다.', color: 'from-purple-100 to-purple-50 text-purple-600' },
  { icon: Camera, label: '브라우저 녹화', description: '실제 브라우저를 조작하며 자연스러운 데모를 녹화합니다.', color: 'from-orange-100 to-orange-50 text-orange-600' },
  { icon: Film, label: '영상 편집', description: '녹화 영상을 전문적으로 편집하고 트랜지션과 자막을 추가합니다.', color: 'from-pink-100 to-pink-50 text-pink-600' },
  { icon: Mic, label: '내레이션 생성', description: '자연스러운 AI 음성으로 전문적인 내레이션을 입혀드립니다.', color: 'from-green-100 to-green-50 text-green-600' },
  { icon: ShieldCheck, label: '품질 검수', description: '최종 영상의 품질을 자동 검수하여 완성도를 보장합니다.', color: 'from-indigo-100 to-indigo-50 text-indigo-600' },
];

function Features() {
  return (
    <section id="features" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            6명의 AI 에이전트가 협업합니다
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            각 분야의 전문 AI 에이전트가 역할을 나누어
            최고 품질의 데모 영상을 만들어냅니다.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg hover:border-purple-100 transition-all duration-300 group"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${f.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.label}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// How It Works
// ============================================================

const STEPS = [
  { icon: Globe, num: '1', title: 'URL 입력', desc: '데모 영상을 만들고 싶은 웹서비스 URL을 입력하세요.' },
  { icon: Sparkles, num: '2', title: 'AI가 영상 생성', desc: '6명의 AI 에이전트가 자동으로 영상을 만듭니다.' },
  { icon: Download, num: '3', title: '영상 다운로드', desc: '완성된 데모 영상을 다운로드하고 바로 사용하세요.' },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            이렇게 간단합니다
          </h2>
          <p className="text-gray-600">3단계만 거치면 데모 영상이 완성됩니다.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connection line (desktop) */}
          <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 border-t-2 border-dashed border-purple-200" />

          {STEPS.map((step, i) => (
            <div key={i} className="text-center relative">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg relative z-10">
                <step.icon className="w-6 h-6 text-white" />
              </div>
              <div className="w-7 h-7 bg-purple-100 text-purple-700 text-xs font-bold rounded-full flex items-center justify-center mx-auto -mt-3 mb-4 relative z-10 border-2 border-white">
                {step.num}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 max-w-xs mx-auto">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Pricing
// ============================================================

function Pricing() {
  const features = [
    '월 5개 데모 영상',
    '최대 2분 길이',
    '1080p 해상도',
    'AI 내레이션 포함',
    '한국어 / 영어 / 일본어',
    'MP4 / WebM / GIF 출력',
  ];

  return (
    <section id="pricing" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">심플한 요금제</h2>
          <p className="text-gray-600">현재 베타 기간 동안 무료로 사용하실 수 있습니다.</p>
        </div>

        <div className="max-w-sm mx-auto">
          <div className="bg-white rounded-2xl border-2 border-purple-200 p-8 shadow-lg relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                <Zap className="w-3 h-3" /> Beta
              </span>
            </div>

            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-gray-900">무료</div>
              <p className="text-sm text-gray-500 mt-1">베타 기간 동안</p>
            </div>

            <ul className="space-y-3 mb-8">
              {features.map((f, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={handleLogin}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-600 transition shadow-md"
            >
              무료로 시작하기
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// CTA
// ============================================================

function Cta() {
  return (
    <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-500">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          지금 바로 데모 영상을 만들어보세요
        </h2>
        <p className="text-purple-100 text-lg mb-8">
          URL만 입력하면, AI가 나머지를 알아서 해드립니다.
        </p>
        <button
          onClick={handleLogin}
          className="px-8 py-3.5 bg-white text-purple-700 rounded-xl font-semibold hover:bg-gray-50 transition shadow-lg flex items-center gap-2 mx-auto"
        >
          무료로 시작하기 <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

// ============================================================
// Footer
// ============================================================

function Footer() {
  return (
    <footer className="bg-gray-900 text-gray-400 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Video className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-300">DemoForge</span>
          </div>
          <p className="text-sm">&copy; 2026 DemoForge. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// Landing Page
// ============================================================

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <Navbar />
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <Cta />
      <Footer />
    </div>
  );
}
